import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import { initWebVitals } from '@/utils/webVitals';

// Buffer errors that occur before Sentry loads
const earlyErrors: unknown[] = [];
const onError = (event: ErrorEvent) => {
  earlyErrors.push(event.error ?? event.message);
};
const onUnhandledRejection = (event: PromiseRejectionEvent) => {
  earlyErrors.push(event.reason);
};
window.addEventListener('error', onError);
window.addEventListener('unhandledrejection', onUnhandledRejection);

// Render React first, then init Sentry asynchronously
createRoot(document.getElementById('root')!).render(<App />);

// Initialize Web Vitals tracking
initWebVitals();

// Defer Sentry initialization until the browser is idle
const initSentry = () => {
  import('@sentry/react').then((Sentry) => {
    Sentry.init({
      dsn: 'https://105de8fd29564952103fadd9d876b3b5@o4510824494858240.ingest.us.sentry.io/4510824602992640',
      sendDefaultPii: true,
      beforeSend(event, hint) {
        const errorValue = hint.originalException;
        const message = typeof errorValue === 'string' ? errorValue : event.message;

        if (message?.includes('Object Not Found Matching Id')) {
          return null;
        }

        if (
          event.exception?.values?.some((e) => e.value?.includes('Object Not Found Matching Id'))
        ) {
          return null;
        }

        if (
          message?.includes('InvalidJWTToken') ||
          event.exception?.values?.some((e) => e.value?.includes('InvalidJWTToken'))
        ) {
          return null;
        }

        if (
          event.exception?.values?.some(
            (e) => e.type === 'AbortError' && e.value?.includes('signal is aborted')
          )
        ) {
          return null;
        }

        if (
          event.exception?.values?.some((e) => e.value?.match(/feature named .* was not found/))
        ) {
          return null;
        }

        return event;
      },
    });

    // Flush buffered errors and remove global handlers
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
    for (const err of earlyErrors) {
      Sentry.captureException(err);
    }
    earlyErrors.length = 0;
  });
};

if (typeof requestIdleCallback === 'function') {
  requestIdleCallback(initSentry);
} else {
  setTimeout(initSentry, 1);
}
